import React from "react";

/**
 * A settings/facts row inside a Card: title + optional sub-line on the left, values and controls
 * on the right. The border-top chain makes any run of these read as one list.
 *
 * `stack` puts the control under the title on a phone instead of beside it. A row's right-hand
 * slot is fine for a value, a switch or a short input, but a control with a width of its own — a
 * Slider — cannot share a phone's width with the title and ends up printed over it.
 */
export const FRow: React.FC<{ title: React.ReactNode; sub?: React.ReactNode; stack?: boolean; children?: React.ReactNode }> = ({
    title,
    sub,
    stack = false,
    children,
}) => (
    <div
        className={`border-t border-[var(--line)] px-5 py-3.5 first:border-t-0 ${
            stack ? "flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4" : "flex items-center gap-4"
        }`}
    >
        <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-medium">{title}</span>
            {sub && <span className="mt-0.5 block text-[12px] leading-snug text-[var(--muted)]">{sub}</span>}
        </span>
        {children}
    </div>
);

/** The blue right-hand value of an FRow; `mu` for director-owned/read-only values. */
export const FVal: React.FC<{ mu?: boolean; mono?: boolean; children: React.ReactNode }> = ({ mu, mono, children }) => (
    <span
        className={`flex-none whitespace-nowrap text-[13px] font-semibold tabular-nums ${mono ? "font-mono text-[12.5px]" : ""} ${
            mu ? "text-[var(--muted)]" : "text-[var(--accent)]"
        }`}
    >
        {children}
    </span>
);
